import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { Breadcrumbs } from "../../../components/Breadcrumbs";
import type { BreadcrumbItem } from "../../../components/Breadcrumbs";
import { useBreadcrumbBack } from "../../../components/useBreadcrumbBack";

import { useAuth } from "../../../providers/AuthProvider";
import { ApiError } from "../../../services/apiClient";
import {
  listControlPoints,
  type ControlPoint,
} from "../../shared/api/controlPoints";
import { listLots, restoreLot, type Lot } from "../api/lots";
import {
  getWorkspace,
  type Workspace,
} from "../../workspaces/api/workspaces";

import { PageShell } from "../../shared/layouts/PageShell";
import { PageHeader } from "../../shared/ui/layouts/PageHeader";
import { SurfaceCard } from "../../shared/ui/surface/SurfaceCard";
import { MetricTile } from "../../shared/ui/MetricTile";

import {
  ArrowClockwise,
  ArrowLeft,
  CalendarBlank,
  ChartLineUp,
  GearSix,
  MapPin,
  MapTrifold,
  Plus,
  PencilSimple,
  Ruler,
  SquaresFourIcon,
} from "@phosphor-icons/react";
import { defaultIconProps } from "../../shared/ui/icons";
import { createLogger } from "../../../utils/logger";

const logger = createLogger("WorkspaceLotsPage");

interface RouteState {
  workspace?: Workspace;
}

const pageStackClasses = "flex flex-col gap-8";
const actionsStackClasses = "flex flex-wrap items-center gap-3";
const secondaryButtonClasses = "pm-btn pm-btn-secondary";
const primaryButtonClasses = "pm-btn pm-btn-primary";
const ghostButtonClasses = "pm-btn pm-btn-ghost";
const toolbarClasses =
  "flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white/90 p-4 shadow-sm sm:flex-row sm:items-center sm:justify-between dark:border-slate-800 dark:bg-slate-900/90";
const searchInputClasses = "pm-input";
const toggleLabelClasses =
  "inline-flex items-center gap-2 text-sm font-medium text-slate-600 dark:text-slate-400";
const checkboxClasses =
  "h-5 w-5 rounded border-slate-300 text-slate-900 accent-slate-900 focus:ring-slate-500 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 dark:accent-slate-100";
const alertClasses =
  "rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-900/50 dark:bg-rose-900/20 dark:text-rose-300";
const listWrapperClasses = "flex flex-col gap-6";
const placeholderClasses =
  "rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-6 text-center text-sm text-slate-500 dark:border-slate-700 dark:bg-slate-800/50 dark:text-slate-400";
const emptyCtaClasses = "pm-btn pm-btn-primary";
const badgeBaseClasses =
  "inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold uppercase tracking-wide";
const roleBadgeClasses: Record<string, string> = {
  owner: "border-indigo-200 bg-indigo-50 text-indigo-700 dark:border-indigo-900/50 dark:bg-indigo-900/30 dark:text-indigo-300",
  editor: "border-green-200 bg-green-50 text-green-700 dark:border-green-900/50 dark:bg-green-900/30 dark:text-green-300",
  viewer: "border-slate-200 bg-slate-100 text-slate-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300",
};
const statusBadgeVariants: Record<"active" | "archived", string> = {
  active: "border-green-200 bg-green-50 text-green-700 dark:border-green-900/50 dark:bg-green-900/30 dark:text-green-300",
  archived: "border-slate-200 bg-slate-100 text-slate-600 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-400",
};
const metricsGridClasses = "grid gap-4 sm:grid-cols-2 xl:grid-cols-3";
const footerDetailClasses =
  "inline-flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400";
const cardActionGroupClasses =
  "flex flex-wrap items-center gap-2 text-sm text-slate-600 dark:text-slate-400";
const cardActionButtonClasses = "pm-btn pm-btn-secondary px-3 py-1.5";
const cardIconButtonClasses = "pm-btn pm-btn-secondary h-10 w-10 p-0";
const archivedCardClasses =
  "border-dashed border-slate-200 bg-slate-50/80 dark:border-slate-700 dark:bg-slate-800/50";

export function WorkspaceLotsPage() {
  const { t } = useTranslation();
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { accessToken } = useAuth();

  const [lots, setLots] = useState<Lot[]>([]);
  const [controlPoints, setControlPoints] = useState<ControlPoint[]>([]);
  const [query, setQuery] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const initialWorkspace = (location.state as RouteState | undefined)?.workspace;
  const [workspaceDetails, setWorkspaceDetails] = useState<Workspace | undefined>(
    initialWorkspace,
  );
  const workspaceLinkState = workspaceDetails
    ? { workspace: workspaceDetails }
    : undefined;

  const loadWorkspaceDetails = useCallback(async () => {
    if (!accessToken || !workspaceId) {
      return;
    }
    try {
      const details = await getWorkspace(accessToken, workspaceId);
      setWorkspaceDetails(details);
    } catch (err) {
      logger.warn("Failed to load workspace", err);
    }
  }, [accessToken, workspaceId]);

  const loadLots = useCallback(async () => {
    if (!accessToken || !workspaceId) {
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const result = await listLots(accessToken, {
        workspace_id: workspaceId,
        search: query.trim() || undefined,
        include_archived: showArchived,
      });
      if (!Array.isArray(result)) {
        logger.warn(
          "Unexpected lots payload, defaulting to empty array",
          result,
        );
        setLots([]);
        return;
      }
      const normalized = result.map((lot) => ({
        ...lot,
        segment_count:
          typeof lot.segment_count === "number"
            ? lot.segment_count
            : Array.isArray(lot.segments)
              ? lot.segments.length
              : 0,
      }));
      setLots(normalized);
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : t("workspaces.lots.load_error");
      setError(message);
    } finally {
      setIsLoading(false);
    }
  }, [accessToken, workspaceId, query, showArchived, t]);

  const loadControlPoints = useCallback(async () => {
    if (!accessToken) {
      return;
    }
    try {
      const result = await listControlPoints(accessToken);
      if (!Array.isArray(result)) {
        logger.warn(
          "Unexpected control points payload, defaulting to empty array",
          result,
        );
        setControlPoints([]);
        return;
      }
      setControlPoints(result);
    } catch (err) {
      logger.warn("Failed to load control points", err);
      setControlPoints([]);
    }
  }, [accessToken]);

  useEffect(() => {
    if (!workspaceId || !accessToken) {
      return;
    }
    if (!workspaceDetails || !workspaceDetails.role) {
      void loadWorkspaceDetails();
    }
  }, [accessToken, workspaceDetails, workspaceId, loadWorkspaceDetails]);

  useEffect(() => {
    void loadLots();
  }, [loadLots]);

  useEffect(() => {
    void loadControlPoints();
  }, [loadControlPoints]);

  const workspaceRole = workspaceDetails?.role;
  const roleBadgeVariant = workspaceRole ?? "viewer";
  const roleBadgeLabel = workspaceRole
    ? t(`workspaces.roles.${workspaceRole}`)
    : workspaceDetails
      ? t("workspaces.roles.viewer")
      : null;
  const canEditLots = workspaceRole === "owner" || workspaceRole === "editor";
  const canManageWorkspace = workspaceRole === "owner";

  const tiePointLookup = useMemo(() => {
    const map = new Map<string, ControlPoint>();
    controlPoints.forEach((point) => {
      map.set(point.id, point);
    });
    return map;
  }, [controlPoints]);

  const formatNumber = useCallback(
    (value: number | null | undefined, fractionDigits = 0, fallback = "—") => {
      if (value == null || Number.isNaN(value)) {
        return fallback;
      }
      return new Intl.NumberFormat(undefined, {
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits,
      }).format(value);
    },
    [],
  );

  const formatDate = useCallback((value: string | null | undefined) => {
    if (!value) {
      return t("common.date_unknown");
    }
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return t("common.date_unknown");
    }
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: "medium",
    }).format(parsed);
  }, [t]);

  const breadcrumbItems = useMemo<BreadcrumbItem[]>(() => {
    return [
      { label: t("workspaces.my_workspaces.title"), to: "/workspaces" },
      { label: workspaceDetails?.name ?? t("workspaces.lots.title") },
      { label: t("workspaces.lots.title") },
    ];
  }, [workspaceDetails?.name, t]);

  const handleBack = useBreadcrumbBack({
    items: breadcrumbItems,
    fallback: { to: "/workspaces" },
  });

  const handleCreateLot = () => {
    if (!workspaceId || !canEditLots) {
      return;
    }
    navigate(`/workspaces/${workspaceId}/lots/new`, {
      state: workspaceLinkState,
    });
  };

  const handleEditLot = (lot: Lot) => {
    if (!workspaceId || !canEditLots) {
      return;
    }
    navigate(`/workspaces/${workspaceId}/lots/${lot.id}/edit`, {
      state: workspaceLinkState,
    });
  };

  const handleRestoreLot = async (lot: Lot) => {
    if (!accessToken || !canEditLots) {
      return;
    }
    try {
      await restoreLot(accessToken, lot.id);
      await loadLots();
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : t("workspaces.lots.restore_error");
      setError(message);
    }
  };

  return (
    <PageShell width="wide">
      <div className={pageStackClasses}>
        <Breadcrumbs items={breadcrumbItems} />

        <PageHeader
          title={
            workspaceDetails?.name ?? t("workspaces.lots.page_title_fallback")
          }
          badge={
            roleBadgeLabel ? (
              <span
                className={`${badgeBaseClasses} ${
                  roleBadgeClasses[roleBadgeVariant] ??
                  roleBadgeClasses.viewer
                }`}
              >
                {roleBadgeLabel}
              </span>
            ) : null
          }
          subtitle={t("workspaces.lots.subtitle")}
          description={workspaceDetails?.description ?? undefined}
          actions={
            <div
              className={actionsStackClasses}
              role="toolbar"
              aria-label={t("workspaces.lots.actions_aria")}
            >
              {canManageWorkspace && workspaceId ? (
                <Link
                  className={secondaryButtonClasses}
                  to={`/workspaces/${workspaceId}/edit`}
                  state={workspaceLinkState}
                >
                  <PencilSimple {...defaultIconProps} aria-hidden />
                  {t("workspaces.lots.edit_workspace_button")}
                </Link>
              ) : null}
              {workspaceId ? (
                <Link
                  className={secondaryButtonClasses}
                  to={`/workspaces/${workspaceId}/lots/map`}
                  state={workspaceLinkState}
                >
                  <MapTrifold {...defaultIconProps} aria-hidden />
                  {t("workspaces.lots.view_map_button")}
                </Link>
              ) : null}
              {canEditLots ? (
                <button
                  type="button"
                  className={primaryButtonClasses}
                  onClick={handleCreateLot}
                >
                  <Plus {...defaultIconProps} aria-hidden />
                  {t("workspaces.lots.new_lot_button")}
                </button>
              ) : null}
              <button
                type="button"
                className={ghostButtonClasses}
                onClick={handleBack}
              >
                <ArrowLeft {...defaultIconProps} aria-hidden />
                {t("actions.back")}
              </button>
            </div>
          }
        />

        <div className={toolbarClasses}>
          <input
            type="search"
            placeholder={t("workspaces.lots.search_placeholder")}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            aria-label={t("workspaces.lots.search_aria")}
            className={searchInputClasses}
          />
          <label className={toggleLabelClasses}>
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(event) => setShowArchived(event.target.checked)}
              className={checkboxClasses}
            />
            {t("workspaces.lots.show_archived")}
          </label>
        </div>

        {error ? (
          <div className={alertClasses} role="alert">
            {error}
          </div>
        ) : null}

        <div className={listWrapperClasses}>
          {isLoading ? (
            <p className={placeholderClasses}>
              {t("workspaces.lots.loading")}
            </p>
          ) : lots.length === 0 ? (
            <SurfaceCard
              tone="interactive"
              link={{
                to: workspaceId
                  ? `/workspaces/${workspaceId}/lots/new`
                  : "/workspaces",
                state: workspaceLinkState,
                label: t("workspaces.lots.empty_state.action"),
              }}
            >
              <SurfaceCard.Body gap="base" textTone="default">
                <h2 className="text-2xl font-semibold text-slate-900 dark:text-slate-100">
                  {t("workspaces.lots.empty_state.title")}
                </h2>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  {t("workspaces.lots.empty_state.description")}
                </p>
                <span className={emptyCtaClasses} aria-hidden="true">
                  <Plus {...defaultIconProps} aria-hidden />
                  {t("workspaces.lots.empty_state.action")}
                </span>
              </SurfaceCard.Body>
            </SurfaceCard>
          ) : (
            lots.map((lot) => {
              const controlPoint = lot.tie_point_id
                ? tiePointLookup.get(lot.tie_point_id)
                : undefined;
              const isArchived = Boolean(lot.archived_at);
              const statusLabel = isArchived
                ? t("workspaces.lots.status_archived")
                : t("workspaces.lots.status_active");
              const trimmedLotName = lot.name.trim();
              const lotLabel =
                trimmedLotName ||
                lot.title_no?.trim() ||
                `lot ${lot.id.slice(0, 8)}`;

              const cardLink =
                workspaceId != null
                  ? {
                      to: `/workspaces/${workspaceId}/lots/${lot.id}`,
                      state: workspaceLinkState,
                      label: t("workspaces.lots.card_link_label", {
                        name: lotLabel,
                      }),
                    }
                  : undefined;

              return (
                <SurfaceCard
                  key={lot.id}
                  as="article"
                tone="interactive"
                className={isArchived ? archivedCardClasses : undefined}
                link={cardLink}
              >
                <SurfaceCard.Header
                  className="mb-2"
                  title={
                    <>
                      <span className="truncate">
                        {trimmedLotName || t("workspaces.lots.untitled")}
                      </span>
                      <span
                        className={`${badgeBaseClasses} ${
                          statusBadgeVariants[isArchived ? "archived" : "active"]
                        }`}
                      >
                        {statusLabel}
                      </span>
                    </>
                  }
                  subtitle={
                    lot.title_no
                      ? t("workspaces.lots.title_prefix", { number: lot.title_no })
                      : t("workspaces.lots.no_title")
                  }
                  action={
                    canEditLots ? (
                      <div className={cardActionGroupClasses}>
                        <button
                          type="button"
                          className={cardIconButtonClasses}
                          onClick={(event) => {
                            event.stopPropagation();
                            handleEditLot(lot);
                          }}
                          aria-label={t("workspaces.lots.edit_aria", {
                            name: lotLabel,
                          })}
                        >
                          <GearSix {...defaultIconProps} aria-hidden />
                        </button>
                        {isArchived ? (
                          <button
                            type="button"
                            className={cardActionButtonClasses}
                            onClick={(event) => {
                              event.stopPropagation();
                              void handleRestoreLot(lot);
                            }}
                          >
                            <ArrowClockwise {...defaultIconProps} aria-hidden />
                            {t("workspaces.lots.restore_button")}
                          </button>
                        ) : null}
                      </div>
                    ) : null
                  }
                />
                <SurfaceCard.Body>
                  <div className={metricsGridClasses}>
                    <MetricTile
                      icon={<SquaresFourIcon {...defaultIconProps} />}
                      label={t("workspaces.lots.segments")}
                      value={formatNumber(lot.segment_count ?? 0)}
                    />
                    <MetricTile
                      icon={<ChartLineUp {...defaultIconProps} />}
                      label={t("workspaces.lots.total_area")}
                      value={
                        lot.computed_area_m2 != null
                          ? `${formatNumber(lot.computed_area_m2, 2)} m²`
                          : t("common.not_available")
                      }
                    />
                    <MetricTile
                      icon={<Ruler {...defaultIconProps} />}
                      label={t("workspaces.lots.traverse_length")}
                      value={
                        lot.traverse_length_m != null
                          ? `${formatNumber(lot.traverse_length_m, 2)} m`
                          : t("common.not_available")
                      }
                    />
                    <MetricTile
                      icon={<MapPin {...defaultIconProps} />}
                      label={t("workspaces.lots.tie_point")}
                      value={
                        controlPoint
                          ? controlPoint.code
                          : t("workspaces.lots.no_tie_point")
                      }
                    />
                    <MetricTile
                      icon={<CalendarBlank {...defaultIconProps} />}
                      label={t("workspaces.lots.created")}
                      value={formatDate(lot.created_at)}
                    />
                  </div>
                </SurfaceCard.Body>
                {isArchived ? (
                  <SurfaceCard.Footer divider>
                    <span className={footerDetailClasses}>
                      <MapTrifold {...defaultIconProps} aria-hidden />
                      {t("workspaces.lots.archived_date", {
                        date: formatDate(lot.archived_at),
                      })}
                    </span>
                  </SurfaceCard.Footer>
                ) : null}
              </SurfaceCard>
            );
          })
        )}
        </div>
      </div>
    </PageShell>
  );
}
